import { Fragment, useState } from "react";
import { Link } from "react-router";
import { Pagination } from "../components/Pagination";
import {
  Alert,
  Card,
  EmptyState,
  Mono,
  PageHeader,
  Select,
  Table,
  TableSkeleton,
  Td,
  Th,
  Tag,
} from "../components/ui";
import { formatDateTime, formatRelative } from "../lib/format";
import { useFleetScheduleRuns, useFleetSchedules } from "../lib/queries";
import type { FleetSchedule } from "../lib/types";

const LIMIT = 25;
/** Runs per page inside an opened history. Ten is a screenful under a table row. */
const RUNS_PER_PAGE = 10;

/**
 * Every scheduled job on the fleet.
 *
 * Read-only on purpose. An operator does not edit a tenant's cron — there is no
 * support case for it, and a job somebody else silently changed is worse than
 * one that is wrong. What this answers is the question a busy machine raises:
 * which tenant is running what, on which box, and how often.
 *
 * The command is shown here and on no customer-facing surface of another
 * tenant's data. It stays out of alerts and emails, which travel.
 */
export function SchedulesPage() {
  const [offset, setOffset] = useState(0);
  const [enabled, setEnabled] = useState<string>("");
  /*
   * One job's history at a time, opened by hand. "It never ran" is the ticket
   * this page exists to close, and until now it could only be answered by
   * opening Dokploy — but the history is an engine round trip per job, so it
   * is fetched when an operator asks for one, not for every row on the page.
   */
  const [openId, setOpenId] = useState<string | null>(null);

  const schedules = useFleetSchedules({
    limit: LIMIT,
    offset,
    ...(enabled === "" ? {} : { enabled: enabled === "true" }),
  });

  const rows = schedules.data?.data ?? [];
  const total = schedules.data?.total ?? 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Scheduled jobs"
        description="Cron jobs tenants run inside their own containers. Billed at the schedule add-on's rate."
      />

      {schedules.error && <Alert>{schedules.error.message}</Alert>}

      <div className="flex flex-wrap items-center gap-3">
        <Select
          value={enabled}
          onChange={(event) => {
            setEnabled(event.target.value);
            setOffset(0);
          }}
          className="w-48"
        >
          <option value="">All jobs</option>
          <option value="true">Enabled only</option>
          <option value="false">Disabled only</option>
        </Select>
        <span className="text-[13px] text-fg-subtle">
          {total} job{total === 1 ? "" : "s"} across the fleet
        </span>
      </div>

      <Card>
        {schedules.isPending ? (
          <TableSkeleton columns={6} />
        ) : rows.length === 0 ? (
          <EmptyState title="No scheduled jobs" description="No tenant has created one yet." />
        ) : (
          <Table
            head={
              <>
                <Th>Organization</Th>
                <Th>App</Th>
                <Th>Job</Th>
                <Th>Runs</Th>
                <Th>Command</Th>
                <Th>Server</Th>
                {/* Not "Runs": the column three to the left already means how
                    often it runs, and two headers with one word between them
                    is how a table stops being readable. */}
                <Th>History</Th>
              </>
            }
          >
            {rows.map((job) => (
              <Fragment key={job.id}>
              <tr>
                <Td>
                  <Link
                    to={`/organizations/${job.organization.id}`}
                    className="text-fg hover:underline"
                  >
                    {job.organization.name}
                  </Link>
                </Td>
                <Td>
                  <div className="text-fg">{job.app.name}</div>
                  <div className="text-[11px] text-fg-subtle">{job.app.state}</div>
                </Td>
                <Td>
                  <div className="text-fg">{job.name}</div>
                  <div className="mt-0.5 flex flex-wrap gap-1">
                    {!job.enabled && <Tag>disabled</Tag>}
                    {/* Off at the engine whatever the column says. A credit
                        suspension turns every job off without touching
                        `enabled`, which is what the customer gets back when
                        they pay — so this is the only thing distinguishing a
                        paused job from a running one on this row. */}
                    {job.enabled && job.organization.suspendedForCredit && (
                      <Tag tone="warn">paused — no credit</Tag>
                    )}
                    {/* Not billed and not running — the difference an operator
                        needs when a tenant asks why a job never fired. */}
                    {!job.provisioned && <Tag tone="bad">not created</Tag>}
                  </div>
                </Td>
                <Td>
                  <div className="text-fg">{job.cronDescription}</div>
                  <div className="text-[11px] text-fg-subtle">
                    <Mono>{job.cronExpression}</Mono> · {job.timezone}
                  </div>
                </Td>
                <Td>
                  <Mono className="text-[12px] break-all text-fg-muted">{job.command}</Mono>
                </Td>
                <Td className="text-fg-muted">
                  {job.server ? (
                    <Link to={`/servers/${job.server.id}`} className="hover:underline">
                      {job.server.name ?? job.server.id.slice(0, 8)}
                    </Link>
                  ) : (
                    "—"
                  )}
                </Td>
                <Td>
                  <button
                    type="button"
                    aria-expanded={openId === job.id}
                    onClick={() => setOpenId(openId === job.id ? null : job.id)}
                    className="text-[13px] text-accent underline-offset-2 hover:underline"
                  >
                    {openId === job.id ? "Hide" : "History"}
                  </button>
                </Td>
              </tr>
              {openId === job.id && (
                <tr>
                  <td colSpan={7} className="border-t border-line bg-raised/40 px-4 py-4">
                    <RunHistory job={job} />
                  </td>
                </tr>
              )}
              </Fragment>
            ))}
          </Table>
        )}
      </Card>

      <Pagination total={total} limit={LIMIT} offset={offset} onChange={setOffset} />
    </div>
  );
}

/**
 * What the engine says this job has done, newest first.
 *
 * Outcomes and timings, and no output: what a command printed is the tenant's
 * data, and the customer's own page is where it is read. This answers the
 * operator's question — did the engine fire it, and how did it end — which the
 * status and the engine's own error message cover.
 */
function RunHistory({ job }: { job: FleetSchedule }) {
  const [offset, setOffset] = useState(0);
  const runs = useFleetScheduleRuns(job.id, { limit: RUNS_PER_PAGE, offset });

  if (!job.provisioned) {
    return (
      <p className="text-[13px] text-fg-muted">
        Never created on the deployment engine, so it has never run. The tenant can retry by
        saving it again.
      </p>
    );
  }
  if (runs.isPending) return <p className="text-[13px] text-fg-subtle">Loading history…</p>;
  if (runs.error) return <Alert>{runs.error.message}</Alert>;
  if (runs.data?.unavailable) {
    return (
      <p className="text-[13px] text-warn">
        The engine on this server could not be asked. This says nothing about whether the job ran.
      </p>
    );
  }
  if (!runs.data || runs.data.data.length === 0) {
    return <p className="text-[13px] text-fg-muted">The engine holds no runs for this job.</p>;
  }

  return (
    <div className="space-y-2">
      <p className="text-[11px] tracking-[0.08em] text-fg-subtle uppercase">
        {runs.data.total} run{runs.data.total === 1 ? "" : "s"} recorded
      </p>
      <ul className="space-y-1.5">
        {runs.data.data.map((run) => (
          <li key={run.id} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-[13px]">
            <Tag tone={run.status === "error" ? "bad" : run.status === "done" ? "good" : "neutral"}>
              {run.status}
            </Tag>
            <span className="text-fg" title={formatDateTime(run.createdAt)}>
              {formatRelative(run.createdAt)}
            </span>
            <span className="text-fg-subtle tabular-nums">{elapsed(run.startedAt, run.finishedAt)}</span>
            {run.errorMessage && <span className="text-fg-muted">{run.errorMessage}</span>}
          </li>
        ))}
      </ul>
      {/* A job at the five-minute floor records 288 runs a day, so the older
          ones need a way back rather than a longer list. */}
      <Pagination
        total={runs.data.total}
        limit={RUNS_PER_PAGE}
        offset={offset}
        onChange={setOffset}
      />
      <p className="text-[11px] text-fg-subtle">
        Output is not shown here. It is the tenant&rsquo;s own data, on their job&rsquo;s page.
      </p>
    </div>
  );
}

/** How long a run took, or a dash while it is still going. */
function elapsed(startedAt: string | null, finishedAt: string | null): string {
  if (!startedAt || !finishedAt) return "—";
  const ms = new Date(finishedAt).getTime() - new Date(startedAt).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "—";
  return ms < 1000 ? `${ms}ms` : `${Math.round(ms / 1000)}s`;
}
